import { getTripEventTypeName } from '@/app/common/converters/newDelegation/tripEventTypeToTextConverter'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { TripVehicleType } from '@/app/constants/delegation/tripVehicleType'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { translateCountryName } from '@/app/common/countryNames'

import React from 'react'
import { createPortal } from 'react-dom'
import DatePickerPopup from '@/ui/date/DatePickerPopup'

import moment from 'moment'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import { useQueryVehiclesList } from '@/lib/api/company/vehicle-list/useQueryVehiclesList'
import { getTripVehicleTypeName } from '@/app/common/converters/newDelegation/tripVehicleTypeToTextConverter'
import { getTmsRouteTypeColor } from '@/app/services/tms/tmsRouteTypeColorResolver'
import { getTmsRouteTypeName } from '@/app/services/tms/tmsRouteTypeNameResolver'
import { TmsRouteType } from '@/app/constants/tmsRouteType'
import { getEventTypeIcon } from '@/app/common/converters/newDelegation/tripEventTypeToIconConverter'
import {
  faCalendar,
  faCaretDownSolid,
  faClock,
  FaIcon,
  faLocationDotSlashSolid,
  faNoteSolid,
} from '@/lib/fa'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import EventsNoteModal from './EventsNoteModal'

const TRIP_EVENTS: TripEventType[] = [
  TripEventType.Begin,
  TripEventType.Finish,
  TripEventType.ChangeCountry,
  TripEventType.TripExclusionStart,
  TripEventType.TripExclusionEnd,
  TripEventType.Arrive,
  TripEventType.Leave,
  TripEventType.Loading,
  TripEventType.Unloading,
]

const VEHICLE_TYPES: TripVehicleType[] = [
  TripVehicleType.NotSet,
  TripVehicleType.Above35Ton,
  TripVehicleType.Below35Ton,
  TripVehicleType.MiniBus,
  TripVehicleType.Bus,
  TripVehicleType.Train,
  TripVehicleType.Ferry,
  TripVehicleType.Plane,
]

const ROUTE_TYPES: TmsRouteType[] = [
  TmsRouteType.Undefined,
  TmsRouteType.Bilateral,
  TmsRouteType.Cabotage,
  TmsRouteType.CabotageEmpty,
  TmsRouteType.CrossTrade,
  TmsRouteType.CrossTradeBilateral,
  TmsRouteType.CrossTradeWrong,
  TmsRouteType.CrossTradeBilateralEmpty,
  TmsRouteType.CrossTradeWrongEmpty,
  TmsRouteType.Transit,
  TmsRouteType.PolishTransit,
  TmsRouteType.Import,
  TmsRouteType.Export,
  TmsRouteType.Empty,
  TmsRouteType.Domestic,
]
const EventsRowItem = ({
  isChecked,
  onSelectChange,
  onSelectMouseDown,
  onRowMouseEnter,
  originalIndex,
  event,
  onUpdateArrayItem,
  companyId,
  isWrong,
}: {
  isChecked: boolean
  onSelectChange?: (index: number, checked: boolean) => void
  onSelectMouseDown?: (index: number) => void
  onRowMouseEnter?: (index: number) => void
  originalIndex: number
  event: any
  onUpdateArrayItem?: (
    arrayName: string,
    index: number,
    updates: any,
    baseline?: any,
  ) => void
  companyId: string
  isWrong?: boolean
}) => {
  const docNumberAtFocus = React.useRef<string | undefined>(undefined)
  const commitDocNumber = () => {
    const started = docNumberAtFocus.current
    docNumberAtFocus.current = undefined
    if (
      originalIndex !== -1 &&
      onUpdateArrayItem &&
      started !== undefined &&
      started !== (event.docNumber || '')
    ) {
      onUpdateArrayItem(
        'events',
        originalIndex,
        { docNumber: event.docNumber },
        { docNumber: started },
      )
    }
  }
  // A row filtered out of the table while its number is being typed gets no blur.
  const commitDocNumberRef = React.useRef(commitDocNumber)
  commitDocNumberRef.current = commitDocNumber
  React.useEffect(() => () => commitDocNumberRef.current(), [])
  const { t, activeLanguage } = useLocalize()
  const lang = activeLanguage?.code || 'pl'
  const { data: countries = [] } = useQueryCountries({ companyId })
  const {
    data: vehiclesData,
    isLoading: vehiclesLoading,
    isError: vehiclesError,
  } = useQueryVehiclesList({ companyId, isActive: true, enabled: !!companyId })
  const vehicles = (
    Array.isArray(vehiclesData) ? vehiclesData : vehiclesData?.vehicles || []
  ).filter((vehicle) => vehicle.isActive)
  const vehicleId = event.vehicleId != null ? String(event.vehicleId) : ''

  const [isEventDropdownOpen, setIsEventDropdownOpen] = React.useState(false)
  const eventDropdownRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isEventDropdownOpen) return undefined
    const handleClickOutside = (e: MouseEvent) => {
      if (
        eventDropdownRef.current &&
        !eventDropdownRef.current.contains(e.target as Node)
      ) {
        setIsEventDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isEventDropdownOpen])

  const handleSelectEvent = (te: TripEventType) => {
    if (originalIndex !== -1 && onUpdateArrayItem) {
      onUpdateArrayItem('events', originalIndex, { tripEvent: te })
    }
    setIsEventDropdownOpen(false)
  }

  const [isNoteModalOpen, setIsNoteModalOpen] = React.useState(false)
  const [noteDraft, setNoteDraft] = React.useState('')

  const openNoteModal = () => {
    setNoteDraft(event.comments || '')
    setIsNoteModalOpen(true)
  }

  const handleSaveNote = ({
    deleteNote = false,
  }: { deleteNote?: boolean } = {}) => {
    if (originalIndex !== -1 && onUpdateArrayItem) {
      onUpdateArrayItem('events', originalIndex, {
        comments: deleteNote ? '' : noteDraft,
      })
    }
    setIsNoteModalOpen(false)
  }

  const [isVehicleDropdownOpen, setIsVehicleDropdownOpen] =
    React.useState(false)
  const vehicleDropdownRef = React.useRef<HTMLDivElement>(null)
  const vehicleMenuRef = React.useRef<HTMLDivElement>(null)
  const [vehicleMenuPosition, setVehicleMenuPosition] =
    React.useState<React.CSSProperties | null>(null)

  React.useEffect(() => {
    if (!isVehicleDropdownOpen) return undefined
    const placeMenu = () => {
      const rect = vehicleDropdownRef.current?.getBoundingClientRect()
      if (!rect) return
      const spaceBelow = window.innerHeight - rect.bottom
      const openUp = spaceBelow < 280 && rect.top > spaceBelow
      setVehicleMenuPosition({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 188)),
        top: openUp ? undefined : rect.bottom + 4,
        bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        maxHeight: Math.min(280, Math.max(0, (openUp ? rect.top : spaceBelow) - 8)),
      })
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        !vehicleDropdownRef.current?.contains(e.target as Node) &&
        !vehicleMenuRef.current?.contains(e.target as Node)
      ) {
        setIsVehicleDropdownOpen(false)
      }
    }
    placeMenu()
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', placeMenu, true)
    window.addEventListener('resize', placeMenu)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', placeMenu, true)
      window.removeEventListener('resize', placeMenu)
    }
  }, [isVehicleDropdownOpen])

  const handleSelectVehicle = (vt: TripVehicleType) => {
    if (originalIndex !== -1 && onUpdateArrayItem) {
      onUpdateArrayItem('events', originalIndex, { vehicleType: vt })
    }
    setIsVehicleDropdownOpen(false)
  }

  const [isRouteTypeDropdownOpen, setIsRouteTypeDropdownOpen] =
    React.useState(false)
  const [routeTypeAlignRight, setRouteTypeAlignRight] = React.useState(false)
  const routeTypeDropdownRef = React.useRef<HTMLDivElement>(null)

  const toggleRouteTypeDropdown = () => {
    if (!isRouteTypeDropdownOpen && routeTypeDropdownRef.current) {
      const rect = routeTypeDropdownRef.current.getBoundingClientRect()
      const menuWidth = 220
      const spaceRight = window.innerWidth - rect.left
      setRouteTypeAlignRight(spaceRight < menuWidth + 20)
    }
    setIsRouteTypeDropdownOpen((prev) => !prev)
  }

  React.useEffect(() => {
    if (!isRouteTypeDropdownOpen) return undefined
    const handleClickOutside = (e: MouseEvent) => {
      if (
        routeTypeDropdownRef.current &&
        !routeTypeDropdownRef.current.contains(e.target as Node)
      ) {
        setIsRouteTypeDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isRouteTypeDropdownOpen])

  const handleSelectRouteType = (rt: TmsRouteType) => {
    if (originalIndex !== -1 && onUpdateArrayItem) {
      onUpdateArrayItem('events', originalIndex, {
        tmsRouteType: rt === TmsRouteType.Undefined ? null : rt,
      })
    }
    setIsRouteTypeDropdownOpen(false)
  }

  const [isCountryDropdownOpen, setIsCountryDropdownOpen] =
    React.useState(false)
  const [countrySearch, setCountrySearch] = React.useState('')
  const countryDropdownRef = React.useRef<HTMLDivElement>(null)
  const countrySearchRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!isCountryDropdownOpen) {
      setCountrySearch('')
      return undefined
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(e.target as Node)
      ) {
        setIsCountryDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    countrySearchRef.current?.focus()
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isCountryDropdownOpen])

  const handleSelectCountry = (country: any) => {
    if (originalIndex !== -1 && onUpdateArrayItem && country) {
      onUpdateArrayItem('events', originalIndex, { country })
    }
    setIsCountryDropdownOpen(false)
  }

  const filteredCountries = React.useMemo(() => {
    if (!countrySearch.trim()) return countries
    const q = countrySearch.trim().toLowerCase()
    return countries.filter((c: any) =>
      translateCountryName(c.name, lang).toLowerCase().includes(q),
    )
  }, [countries, countrySearch, lang])

  const toISODate = (date: any): string | null => {
    if (!date) return null
    if (moment.isMoment(date)) return date.format('YYYY-MM-DD')
    if (typeof date === 'string') return moment(date).format('YYYY-MM-DD')
    return null
  }

  const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false)
  const [dateInputValue, setDateInputValue] = React.useState('')
  const dateContainerRef = React.useRef<HTMLDivElement>(null)
  const datePopupRef = React.useRef<HTMLDivElement>(null)
  const dateInputRef = React.useRef<HTMLInputElement>(null)
  const dateCursorPosRef = React.useRef<number | null>(null)
  const [datePopupPosition, setDatePopupPosition] = React.useState({
    top: 0,
    left: 0,
  })

  React.useEffect(() => {
    setDateInputValue(toISODate(event.date) || '')
  }, [event.date])

  React.useEffect(() => {
    if (dateCursorPosRef.current !== null && dateInputRef.current) {
      const pos = dateCursorPosRef.current
      dateInputRef.current.setSelectionRange(pos, pos)
      dateCursorPosRef.current = null
    }
  })

  const formatDateWithDashes = (digits: string): string => {
    let result = ''
    for (let i = 0; i < digits.length; i++) {
      if (i === 4 || i === 6) result += '-'
      result += digits[i]
    }
    return result
  }

  const parseDateInput = (input: string): string | null => {
    const trimmed = input.trim()
    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!match) return null
    const year = parseInt(match[1], 10)
    const month = parseInt(match[2], 10)
    const day = parseInt(match[3], 10)
    if (year < 1900 || year > 2100) return null
    if (month < 1 || month > 12) return null
    if (day < 1 || day > 31) return null
    const date = new Date(year, month - 1, day)
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    )
      return null
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  }

  const commitDate = (newDate: string | null) => {
    if (originalIndex === -1 || !onUpdateArrayItem) return
    const currentTime = event.date
      ? moment(event.date).format('HH:mm')
      : '00:00'
    onUpdateArrayItem('events', originalIndex, {
      date: newDate ? moment(`${newDate} ${currentTime}`) : undefined,
    })
  }

  const openDatePopup = () => {
    if (!dateContainerRef.current) return
    const rect = dateContainerRef.current.getBoundingClientRect()
    setDatePopupPosition({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
    })
    setIsDatePickerOpen(true)
  }

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    const cursorPos = e.target.selectionStart ?? newValue.length
    const newDigits = newValue.replace(/\D/g, '').slice(0, 8)
    const formatted = formatDateWithDashes(newDigits)

    const digitsBeforeCursor = newValue
      .slice(0, cursorPos)
      .replace(/\D/g, '').length
    let newCursorPos = 0
    let digitCount = 0
    for (let i = 0; i < formatted.length; i++) {
      if (digitCount === digitsBeforeCursor) break
      newCursorPos = i + 1
      if (formatted[i] !== '-') digitCount++
    }
    if (
      newCursorPos < formatted.length &&
      formatted[newCursorPos] === '-' &&
      newValue.length >= dateInputValue.length
    ) {
      newCursorPos++
    }

    dateCursorPosRef.current = newCursorPos
    setDateInputValue(formatted)
  }

  const handleDateInputBlur = () => {
    const parsed = parseDateInput(dateInputValue)
    if (parsed) {
      commitDate(parsed)
    } else if (dateInputValue === '') {
      commitDate(null)
    } else {
      setDateInputValue(toISODate(event.date) || '')
    }
  }

  const handleDateInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      dateInputRef.current?.blur()
    }
  }

  const [timeInputValue, setTimeInputValue] = React.useState('')
  const timeInputRef = React.useRef<HTMLInputElement>(null)
  const timeCursorPosRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    setTimeInputValue(event.date ? moment(event.date).format('HH:mm') : '')
  }, [event.date])

  React.useEffect(() => {
    if (timeCursorPosRef.current !== null && timeInputRef.current) {
      const pos = timeCursorPosRef.current
      timeInputRef.current.setSelectionRange(pos, pos)
      timeCursorPosRef.current = null
    }
  })

  const formatTimeWithColon = (digits: string): string => {
    let result = ''
    for (let i = 0; i < digits.length; i++) {
      if (i === 2) result += ':'
      result += digits[i]
    }
    return result
  }

  const parseTimeInput = (input: string): string | null => {
    const trimmed = input.trim()
    const match = trimmed.match(/^(\d{2}):(\d{2})$/)
    if (!match) return null
    const hours = parseInt(match[1], 10)
    const minutes = parseInt(match[2], 10)
    if (hours < 0 || hours > 23) return null
    if (minutes < 0 || minutes > 59) return null
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  }

  const commitTime = (newTime: string | null) => {
    if (originalIndex === -1 || !onUpdateArrayItem) return
    const currentDate = event.date
      ? moment(event.date).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD')
    onUpdateArrayItem('events', originalIndex, {
      date: moment(`${currentDate} ${newTime ?? '00:00'}`),
    })
  }

  const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    const cursorPos = e.target.selectionStart ?? newValue.length
    const newDigits = newValue.replace(/\D/g, '').slice(0, 4)
    const formatted = formatTimeWithColon(newDigits)

    const digitsBeforeCursor = newValue
      .slice(0, cursorPos)
      .replace(/\D/g, '').length
    let newCursorPos = 0
    let digitCount = 0
    for (let i = 0; i < formatted.length; i++) {
      if (digitCount === digitsBeforeCursor) break
      newCursorPos = i + 1
      if (formatted[i] !== ':') digitCount++
    }
    if (
      newCursorPos < formatted.length &&
      formatted[newCursorPos] === ':' &&
      newValue.length >= timeInputValue.length
    ) {
      newCursorPos++
    }

    timeCursorPosRef.current = newCursorPos
    setTimeInputValue(formatted)
  }

  const handleTimeInputBlur = () => {
    const parsed = parseTimeInput(timeInputValue)
    if (parsed) {
      commitTime(parsed)
    } else if (timeInputValue === '') {
      commitTime(null)
    } else {
      setTimeInputValue(event.date ? moment(event.date).format('HH:mm') : '')
    }
  }

  const handleTimeInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      timeInputRef.current?.blur()
    }
  }

  const handleDatePopupSubmit = (fullDate: string) => {
    setDateInputValue(fullDate)
    commitDate(fullDate)
    setIsDatePickerOpen(false)
  }

  React.useEffect(() => {
    if (!isDatePickerOpen) return undefined
    const handler = (e: MouseEvent) => {
      if (
        dateContainerRef.current &&
        !dateContainerRef.current.contains(e.target as Node) &&
        datePopupRef.current &&
        !datePopupRef.current.contains(e.target as Node)
      ) {
        setIsDatePickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isDatePickerOpen])
  return (
    <tr
      onMouseEnter={() => onRowMouseEnter?.(originalIndex)}
      className={`tw-border-b tw-border-base-200 ${
        isWrong
          ? 'tw-bg-red-50'
          : isChecked
            ? 'tw-bg-secondary hover:tw-bg-secondary'
            : 'hover:tw-bg-base-50'
      }`}
    >
      <TableCheckbox
        disabled={false}
        checked={isChecked}
        isHovered={isChecked}
        onChange={(checked) => onSelectChange?.(originalIndex, checked)}
        onMouseDown={() => onSelectMouseDown?.(originalIndex)}
      />
      <td
        className="tw-px-3 tw-py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={eventDropdownRef} className="tw-relative">
          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between"
            onClick={() => setIsEventDropdownOpen((prev) => !prev)}
          >
            <div className="tw-flex tw-items-center tw-gap-1">
              {isWrong && (
                <span
                  className="tw-flex-shrink-0 tw-text-red-500"
                  title={t('businessTrip.invalid-event-order')}
                >
                  &#9888;
                </span>
              )}
              <FaIcon icon={getEventTypeIcon(event?.tripEvent)} />
              <p className="tw-text-sm tw-text-content-base">
                {getTripEventTypeName(event.tripEvent, t)}
              </p>
            </div>
            <FaIcon icon={faCaretDownSolid} />
          </div>
          {isEventDropdownOpen && (
            <div className="tw-absolute tw-left-0 tw-top-full tw-z-50 tw-mt-1 tw-min-w-[220px] tw-rounded tw-border tw-border-base-300 tw-bg-white tw-py-1 tw-shadow-lg">
              {TRIP_EVENTS.map((te) => (
                <div
                  key={te}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-100"
                  onClick={() => handleSelectEvent(te)}
                >
                  <FaIcon icon={getEventTypeIcon(te)} />
                  <span>{getTripEventTypeName(te, t)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </td>
      <td
        className="tw-px-3 tw-py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          ref={dateContainerRef}
          className="tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-rounded tw-py-1"
        >
          <FaIcon
            icon={faCalendar}
            className="tw-cursor-pointer"
            onClick={() => {
              if (isDatePickerOpen) {
                setIsDatePickerOpen(false)
              } else {
                openDatePopup()
              }
            }}
          />
          <input
            ref={dateInputRef}
            type="text"
            value={dateInputValue}
            onChange={handleDateInputChange}
            onBlur={handleDateInputBlur}
            onKeyDown={handleDateInputKeyDown}
            placeholder="RRRR-MM-DD"
            className="tw-min-w-0 tw-w-full tw-bg-transparent tw-text-sm tw-text-content-base tw-placeholder-content-base/50 focus:tw-outline-none"
          />
        </div>
        {isDatePickerOpen &&
          createPortal(
            <div
              ref={datePopupRef}
              className="tw-absolute tw-z-[9999] tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg"
              style={{
                top: datePopupPosition.top,
                left: datePopupPosition.left,
              }}
            >
              <DatePickerPopup
                value={toISODate(event.date)}
                submit={handleDatePopupSubmit}
              />
            </div>,
            document.body,
          )}
      </td>
      <td
        className="tw-px-3 tw-py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-rounded tw-py-1">
          <FaIcon icon={faClock} />
          <input
            ref={timeInputRef}
            type="text"
            value={timeInputValue}
            onChange={handleTimeInputChange}
            onBlur={handleTimeInputBlur}
            onKeyDown={handleTimeInputKeyDown}
            placeholder="00:00"
            className="tw-min-w-0 tw-w-full tw-bg-transparent tw-text-sm tw-text-content-base tw-placeholder-content-base/50 focus:tw-outline-none"
          />
        </div>
      </td>
      <td
        className="tw-px-3 tw-py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={countryDropdownRef} className="tw-relative">
          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2"
            onClick={() => setIsCountryDropdownOpen((prev) => !prev)}
          >
            <p className="tw-text-sm tw-text-content-base">
              {event.country
                ? translateCountryName(event.country.name, lang)
                : t('newDelegation.accomodation.choose-country')}
            </p>
            <FaIcon icon={faCaretDownSolid} />
          </div>
          {isCountryDropdownOpen && (
            <div className="tw-absolute tw-left-0 tw-top-full tw-z-50 tw-mt-1 tw-flex tw-max-h-[320px] tw-min-w-[220px] tw-flex-col tw-rounded tw-border tw-border-base-300 tw-bg-white tw-shadow-lg">
              <div className="tw-flex-shrink-0 tw-border-b tw-border-base-300 tw-p-2">
                <input
                  ref={countrySearchRef}
                  type="text"
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  placeholder={t('menu.search')}
                  className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1 tw-text-sm focus:tw-border-primary focus:tw-outline-none"
                />
              </div>
              <div className="tw-flex-1 tw-overflow-y-auto tw-py-1">
                {filteredCountries.map((c: any) => (
                  <div
                    key={c.id}
                    className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-100"
                    onClick={() => handleSelectCountry(c)}
                  >
                    <span>{translateCountryName(c.name, lang)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </td>
      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          value={event.city || ''}
          onChange={(e) => {
            if (originalIndex !== -1 && onUpdateArrayItem) {
              onUpdateArrayItem('events', originalIndex, {
                city: e.target.value,
              })
            }
          }}
          className="tw-w-full tw-rounded tw-bg-transparent tw-py-1 tw-text-sm"
          placeholder="Wpisz..."
        />
      </td>
      <td
        className="tw-px-3 tw-py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={vehicleDropdownRef} className="tw-relative">
          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2"
            onClick={() => setIsVehicleDropdownOpen((prev) => !prev)}
          >
            <p className="tw-text-sm tw-text-content-base">
              {event.vehicleType != null &&
              event.vehicleType !== TripVehicleType.NotSet
                ? getTripVehicleTypeName(event.vehicleType, t)
                : '-'}
            </p>
            <FaIcon icon={faCaretDownSolid} />
          </div>
          {isVehicleDropdownOpen && vehicleMenuPosition &&
            createPortal(
              <div
                ref={vehicleMenuRef}
                style={vehicleMenuPosition}
                className="tw-fixed tw-z-[9999] tw-min-w-[180px] tw-overflow-y-auto tw-rounded tw-border tw-border-base-300 tw-bg-white tw-py-1 tw-shadow-lg"
              >
                {VEHICLE_TYPES.map((vt) => (
                  <div
                    key={vt}
                    className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-100"
                    onClick={() => handleSelectVehicle(vt)}
                  >
                    <span>
                      {vt === TripVehicleType.NotSet
                        ? '-'
                        : getTripVehicleTypeName(vt, t)}
                    </span>
                  </div>
                ))}
              </div>,
              document.body,
            )}
        </div>
      </td>
      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <select
          value={vehicleId}
          onChange={(e) => {
            if (originalIndex !== -1 && onUpdateArrayItem) {
              const vehicle = vehicles.find(
                (v) => String(v.id) === e.target.value,
              )
              onUpdateArrayItem('events', originalIndex, {
                vehicleId: vehicle ? String(vehicle.id) : null,
                registrationNumber: vehicle?.registrationNumber || '',
              })
            }
          }}
          aria-label={t('businessTrip.col-reg-number')}
          disabled={vehiclesLoading || vehiclesError}
          title={vehiclesError ? t('shared.vehicle-picker.error') : undefined}
          className="tw-w-full tw-rounded tw-bg-transparent tw-py-1 tw-text-sm"
        >
          <option value="">
            {vehiclesLoading
              ? t('shared.vehicle-picker.loading')
              : vehiclesError
                ? t('shared.vehicle-picker.error')
                : !vehicleId && event.registrationNumber
                  ? event.registrationNumber
                  : '-'}
          </option>
          {vehicleId && !vehicles.some((v) => String(v.id) === vehicleId) && (
            <option value={vehicleId} disabled>
              {event.registrationNumber || vehicleId}
            </option>
          )}
          {vehicles.map((vehicle) => (
            <option key={vehicle.id} value={vehicle.id}>
              {vehicle.registrationNumber || `#${vehicle.id}`}
            </option>
          ))}
          {!vehiclesLoading && !vehiclesError && vehicles.length === 0 && (
            <option disabled>{t('shared.picker.no-results')}</option>
          )}
        </select>
      </td>

      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          value={event.docNumber || ''}
          onChange={(e) => {
            if (originalIndex !== -1 && onUpdateArrayItem) {
              onUpdateArrayItem('events', originalIndex, {
                docNumber: e.target.value,
              })
            }
          }}
          onFocus={() => {
            if (docNumberAtFocus.current === undefined) {
              docNumberAtFocus.current = event.docNumber || ''
            }
          }}
          onBlur={() => {
            // Switching windows blurs the field mid-typing; the number is not final yet.
            if (!document.hasFocus()) return
            commitDocNumber()
          }}
          className="tw-w-full tw-rounded tw-bg-transparent tw-py-1 tw-text-sm"
          placeholder="Wpisz..."
        />
      </td>
      <td
        className="tw-px-3 tw-py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 hover:tw-opacity-80"
          onClick={openNoteModal}
        >
          <FaIcon icon={faNoteSolid} className="tw-text-primary" />
          <p className="tw-text-sm tw-text-primary">
            {event.comments ? t('notes.edit-note') : t('notes.add-note')}
          </p>
        </div>
        {isNoteModalOpen && (
          <EventsNoteModal
            event={event}
            handleClose={() => setIsNoteModalOpen(false)}
            noteDraft={noteDraft}
            setNoteDraft={setNoteDraft}
            handleSave={handleSaveNote}
          />
        )}
      </td>
      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        {event.tripEvent === TripEventType.Loading ||
        event.tripEvent === TripEventType.Unloading ? (
          <div ref={routeTypeDropdownRef} className="tw-relative">
            <div
              className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2"
              onClick={toggleRouteTypeDropdown}
            >
              <div className="tw-flex tw-items-center tw-gap-2">
                <span
                  className="tw-h-3 tw-w-3 tw-flex-shrink-0 tw-rounded-sm"
                  style={{
                    backgroundColor: getTmsRouteTypeColor(
                      event.tmsRouteType ?? TmsRouteType.Undefined,
                    ),
                  }}
                />
                <p className="tw-text-sm tw-text-content-base">
                  {t(
                    getTmsRouteTypeName(
                      event.tmsRouteType ?? TmsRouteType.Undefined,
                    ),
                  )}
                </p>
              </div>
              <FaIcon icon={faCaretDownSolid} />
            </div>
            {isRouteTypeDropdownOpen && (
              <div
                className={`tw-absolute tw-top-full tw-z-50 tw-mt-1 tw-flex tw-max-h-[400px] tw-min-w-[220px] tw-flex-col tw-overflow-y-auto tw-rounded tw-border tw-border-base-300 tw-bg-white tw-py-1 tw-shadow-lg ${
                  routeTypeAlignRight ? 'tw-right-0' : 'tw-left-0'
                }`}
              >
                {ROUTE_TYPES.map((rt) => (
                  <div
                    key={rt}
                    className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-100"
                    onClick={() => handleSelectRouteType(rt)}
                  >
                    <span
                      className="tw-h-3 tw-w-3 tw-flex-shrink-0 tw-rounded-sm"
                      style={{ backgroundColor: getTmsRouteTypeColor(rt) }}
                    />
                    <span>{t(getTmsRouteTypeName(rt))}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="tw-flex tw-items-center tw-justify-center">
            <Tooltip
              portal
              text={
                event.tripEvent === TripEventType.ChangeCountry
                  ? 'Wyłącz z delegowania'
                  : 'Dostępne tylko dla zmiany kraju'
              }
            >
              <FaIcon
                icon={faLocationDotSlashSolid}
                onClick={() => {
                  if (
                    event.tripEvent === TripEventType.ChangeCountry &&
                    originalIndex !== -1 &&
                    onUpdateArrayItem
                  ) {
                    onUpdateArrayItem('events', originalIndex, {
                      isTransit: !event.isTransit,
                    })
                  }
                }}
                className={
                  event.tripEvent === TripEventType.ChangeCountry
                    ? `tw-cursor-pointer ${
                        event.isTransit
                          ? 'tw-text-primary'
                          : 'tw-text-gray-400 tw-opacity-80 hover:tw-opacity-100'
                      }`
                    : 'tw-cursor-not-allowed tw-text-gray-300 tw-opacity-40'
                }
              />
            </Tooltip>
          </div>
        )}
      </td>
    </tr>
  )
}

export default EventsRowItem
